import { useEffect, useRef, useState, useCallback } from 'react';
import { API_URL } from '../config';
import { formatTestimonialSnippet } from '../utils/formatters';

const MAX_CACHE_SIZE = 50;
const snippetCache = new Map();
const fullCache = new Map();

function cacheSet(cache, key, value) {
  if (cache.has(key)) {
    cache.delete(key);
  } else if (cache.size >= MAX_CACHE_SIZE) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }
  cache.set(key, value);
}

export default function useCourseTestimonials(cursoId) {
  const [loading, setLoading] = useState(false);
  const [snippet, setSnippet] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  
  // Track active controllers
  const activeControllersRef = useRef(new Set());

  useEffect(() => {
    if (!cursoId) {
      setSnippet(null);
      setTotal(0);
      return;
    }
    if (snippetCache.has(cursoId)) {
      const cached = snippetCache.get(cursoId);
      setSnippet(cached.snippet);
      setTotal(cached.total || 0);
      setError(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const controller = new AbortController();
    activeControllersRef.current.add(controller);
    const { signal } = controller;
    
    const encodedId = encodeURIComponent(cursoId);
    
    fetch(`${API_URL}/api/testimonios?cursoId=${encodedId}&limit=1`, { signal })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch testimonials');
        return res.json();
      })
      .then(data => {
        const items = Array.isArray(data.items) ? data.items : [];
        const first = items[0] || null;
        const count = Number(data.total || items.length || 0);
        
        let s = null;
        if (first) {
            const text = typeof first.text === 'string' ? first.text : '';
            const author = first.author || first.autor || '';
            const date = first.date || first.fecha || null;
            s = formatTestimonialSnippet(text, author, date);
        }
        
        cacheSet(snippetCache, cursoId, { snippet: s, total: count });
        setSnippet(s);
        setTotal(count);
        setError(null);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
            console.error(err);
            setError(err);
        }
      })
      .finally(() => {
        // Always clear loading if this request finished (even if aborted, though usually we might not want to update state on unmount)
        // However, the instruction says: "always call setLoading(false) in the final .finally() (do not gate it on !signal.aborted)"
        setLoading(false);
        activeControllersRef.current.delete(controller);
      });

    const controllers = activeControllersRef.current;
    return () => {
      controller.abort();
      controllers.delete(controller);
    };
  }, [cursoId]);

  const fetchFull = useCallback(async (page = 1) => {
    if (!cursoId) return { items: [], total: 0, page };
    const cacheKey = `${cursoId}:${page}`;
    if (fullCache.has(cacheKey)) return fullCache.get(cacheKey);
    
    const controller = new AbortController();
    activeControllersRef.current.add(controller);
    const { signal } = controller;
    
    try {
        const encodedId = encodeURIComponent(cursoId);
        const res = await fetch(`${API_URL}/api/testimonios?cursoId=${encodedId}&limit=10&page=${page}`, { signal });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const totalItems = Number(data.total || items.length || 0);
        
        const result = { items, total: totalItems, page };
        cacheSet(fullCache, cacheKey, result);
        return result;
    } catch (err) {
        if (err.name !== 'AbortError') throw err;
        return { items: [], total: 0, page };
    } finally {
        activeControllersRef.current.delete(controller);
    }
  }, [cursoId]);

  return { loading, snippet, total, error, fetchFull };
}
